/**
 * Units Mapping View
 * 
 * Page for mapping units across different channels.
 * Now uses module's own hooks and services.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useToast } from '@context/ToastContext';

// Import from module hooks
import {
  useGetAirbnbUnitsQuery,
  useGetBookingUnitsQuery,
  useGetGathernUnitsQuery,
  useGetMsoolUnits,
  useMapUnits,
  useUnmapUnits,
} from '../../hooks';

// Import from module types
import { ChannelType, ChannelName, IUnitSelection, IMsoolUnit, IUnmapUnitsRequest, ILinkedUnit, IChannelMappingResult, IMappingUnitRequestDto } from '../../types';
import { T_KEYS } from '../../constants';

// Import custom hook from module
import { useUnitsMapping } from '../../hooks/useUnitsMapping';

// Import components from original location until moved
import {
  EmptyState,
  OnboardingModal,
  MsoolUnitsCard,
  MergeButton,
  UnmapConfirmModal,
  MappingErrorModal,
  HeaderSection,
  ChannelCardComponent,
} from '../../components/UnitsMapping';
import PropertiesEmptyState from '../../components/PropertiesEmptyState';

// Import styles from module
import { PageContainer, MainContent } from '../../styles/UnitsMappingStyles';

const UnitsMappingView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  
  // Check if user came from "Add Units" button
  const isAddingUnits = (location.state as { isAddingUnits?: boolean })?.isAddingUnits ?? false;

  // Custom hook for selection management
  const {
    selectedUnits,
    isUnitSelected,
    toggleUnitSelection,
    clearSelection,
    selectedChannels,
    canMerge,
    selectionCount,
    getMapPayload,
    channelTypeToName,
  } = useUnitsMapping();

  // Modal states
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showUnmapConfirmModal, setShowUnmapConfirmModal] = useState(false);
  const [unmapTargetUnit, setUnmapTargetUnit] = useState<IMsoolUnit | null>(null);
  const [showMappingErrorModal, setShowMappingErrorModal] = useState(false);
  const [mappingErrorResult, setMappingErrorResult] = useState<IChannelMappingResult | null>(null);

  // Selected Msool unit for mapping
  const [selectedMsoolUnit, setSelectedMsoolUnit] = useState<IMsoolUnit | null>(null);

  // UI states
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set(['airbnb', 'booking', 'gathern']));
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({
    airbnb: '',
    booking: '',
    gathern: '',
  });
  const [statusFilters, setStatusFilters] = useState<Record<string, string>>({
    airbnb: 'all',
    booking: 'all',
    gathern: 'all',
  });

  // Fetch units from all channels using module hooks
  const { data: airbnbUnits, isLoading: loadingAirbnb } = useGetAirbnbUnitsQuery();
  const { data: bookingUnits, isLoading: loadingBooking } = useGetBookingUnitsQuery();
  const { data: gathernUnits, isLoading: loadingGathern } = useGetGathernUnitsQuery();
  
  // Fetch Msool mapped units using module hook
  const { data: msoolUnitsData, isLoading: loadingMsool } = useGetMsoolUnits();
  
  // Mutations using module hooks
  const { mutate: mapUnits, isPending: isMapping } = useMapUnits();
  const { mutate: unmapUnits, isPending: isUnmapping } = useUnmapUnits();

  // Check if first time user — exclude units that only have Airbnb linked (auto-merged)
  const hasNoMappedUnits = useMemo(() => {
    if (!msoolUnitsData?.units || msoolUnitsData.units.length === 0) return true;
    // A unit counts as "mapped" only if it has at least one non-Airbnb active channel
    return !msoolUnitsData.units.some(unit =>
      unit.channels?.some(c => c.status === true && c.channelType !== ChannelType.Airbnb)
    );
  }, [msoolUnitsData]);

  // Check if onboarding modal should be shown
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('units_mapping_onboarding_seen');
    if (!hasSeenOnboarding && hasNoMappedUnits) {
      setShowOnboardingModal(true);
    }
  }, [hasNoMappedUnits]);

  // Combine all units data
  const allUnitsData = useMemo(() => ({
    airbnb: airbnbUnits?.units || [],
    booking: bookingUnits?.units || [],
    gathern: gathernUnits?.units || [],
  }), [airbnbUnits, bookingUnits, gathernUnits]);

  // Check if there are any units to map
  const hasUnitsToMap = useMemo(() => {
    const totalUnits = 
    (bookingUnits?.units?.length || 0) + 
    (airbnbUnits?.units?.length || 0) + 
      (gathernUnits?.units?.length || 0);
    return totalUnits > 0;
  }, [airbnbUnits, bookingUnits, gathernUnits]);

  // Loading state
  const isLoadingUnits = loadingAirbnb || loadingBooking || loadingGathern || loadingMsool;

  // Check if selected Msool unit has Airbnb linked
  const hasAirbnbSelected = useMemo(() => {
    if (!selectedMsoolUnit) return false;
    return selectedMsoolUnit.channels?.some(
      (channel: ILinkedUnit) => channel.channelType === ChannelType.Airbnb && channel.status
    ) ?? false;
  }, [selectedMsoolUnit]);

  // Get Airbnb unit from selected Msool unit
  const airbnbFromMsoolUnit = useMemo(() => {
    if (!selectedMsoolUnit) return null;
    return selectedMsoolUnit.channels?.find(
      (channel: ILinkedUnit) => channel.channelType === ChannelType.Airbnb && channel.status
    ) ?? null;
  }, [selectedMsoolUnit]);

  // Custom canMerge: requires Msool unit with Airbnb + at least one Booking/Gathern unit selected
  const canMergeWithMsool = useMemo(() => {
    return hasAirbnbSelected && selectedUnits.length >= 1;
  }, [hasAirbnbSelected, selectedUnits]);

  // Custom selection count: Airbnb from Msool + selected Booking/Gathern units
  const totalSelectionCount = useMemo(() => {
    return (hasAirbnbSelected ? 1 : 0) + selectedUnits.length;
  }, [hasAirbnbSelected, selectedUnits]);

  // Build mapping payload with selected Booking/Gathern units only
  // The propertyId from selected Msool unit links the new units to that property
  const getMapPayloadWithAirbnb = useCallback((): IMappingUnitRequestDto[] => {
    // Only include selected Booking/Gathern units, not the Airbnb unit
    return selectedUnits.map((unit) => ({
      userChannelId: unit.userChannelId,
      channelType: unit.channelType,
      channelListingId: unit.channelListingId,
      propertyId: selectedMsoolUnit?.propertyId ?? null,
    }));
  }, [selectedUnits, selectedMsoolUnit]);

  // Channel configuration (excluding Airbnb - it's no longer displayed as a separate card)
  const channelConfig = useMemo(() => ({
    booking: {
      name: t(T_KEYS.UNITS_MAPPING_BOOKING_UNITS),
      icon: 'B',
      color: '#003580',
      bgColor: '#003580',
      channelType: ChannelType.Booking,
    },
    gathern: {
      name: t(T_KEYS.UNITS_MAPPING_GATHERN_UNITS),
      icon: 'G',
      color: '#1a73e8',
      bgColor: '#1a73e8',
      channelType: ChannelType.Gathern,
    },
  }), [t]);

  // Filter units based on search and status
  const getFilteredUnits = useCallback((channelKey: string, units: any[]) => {
    const search = searchQueries[channelKey]?.toLowerCase() || '';
    const status = statusFilters[channelKey] || 'all';

    return units.filter((unit: any) => {
      const matchesSearch = !search || 
        unit.listingName?.toLowerCase().includes(search) ||
        unit.channelListingId?.toLowerCase().includes(search);

      let matchesStatus = true;
      if (status === 'mapped') {
        matchesStatus = unit.isMapped === true;
      } else if (status === 'not_mapped') {
        matchesStatus = unit.isMapped !== true;
      }

      return matchesSearch && matchesStatus;
    });
  }, [searchQueries, statusFilters]);

  // Handlers
  const handleBackToMain = useCallback(() => {
    if (isAddingUnits) {
      navigate('/channels/properties');
    } else {
      navigate('/channels');
    }
  }, [navigate, isAddingUnits]);

  const handleChannelToggle = useCallback((channelId: string) => {
    setExpandedChannels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(channelId)) {
        newSet.delete(channelId);
      } else {
        newSet.add(channelId);
      }
      return newSet;
    });
  }, []);

  const handleSearchChange = useCallback((channelKey: string, query: string) => {
    setSearchQueries(prev => ({ ...prev, [channelKey]: query }));
  }, []);

  const handleStatusFilterChange = useCallback((channelKey: string, status: string) => {
    setStatusFilters(prev => ({ ...prev, [channelKey]: status }));
  }, []);

  const handleUnitToggle = useCallback((unit: any, channelKey: ChannelName) => {
    const config = channelConfig[channelKey as keyof typeof channelConfig];
    if (!config) return; // Skip if channel not in config (e.g., airbnb)
    
    const selection: IUnitSelection = {
      unitId: unit.id,
      userChannelId: unit.userChannelId,
      channelListingId: unit.channelListingId,
      channel: channelKey,
      channelType: config.channelType,
      name: unit.listingName,
      image: unit.listingImage,
    };
    toggleUnitSelection(selection);
    
    setExpandedChannels(prev => {
      const newSet = new Set(prev);
      newSet.delete(channelKey);
      return newSet;
    });
  }, [channelConfig, toggleUnitSelection]);

  const handleOnboardingClose = useCallback(() => {
    localStorage.setItem('units_mapping_onboarding_seen', 'true');
    setShowOnboardingModal(false);
  }, []);

  const handleOnboardingStart = useCallback(() => {
    localStorage.setItem('units_mapping_onboarding_seen', 'true');
    setShowOnboardingModal(false);
  }, []);

  const handleMergeConfirm = useCallback(() => {
    const payload = getMapPayloadWithAirbnb();
    mapUnits(payload, {
      onSuccess: (response: any) => {
        // Check if response contains partial failure with detailed error info
        const result = response?.result;
        if (result && !result.success && result.channelResults) {
          // Find the first failed channel result with error details
          const failedResult = result.channelResults.find(
            (cr: IChannelMappingResult) => !cr.success && cr.error
          );
          
          if (failedResult) {
            // Show error modal with detailed error info
            setMappingErrorResult(failedResult);
            setShowMappingErrorModal(true);
            
            // If there were some successes, still clear selection
            if (result.successCount > 0) {
              clearSelection();
              setSelectedMsoolUnit(null);
            }
            return;
          }
        }
        
        // Full success
        showToast(t(T_KEYS.UNITS_MAPPING_SUCCESS_MERGED), { severity: 'success' });
        clearSelection();
        setSelectedMsoolUnit(null);
      },
      onError: (error: any) => {
        // Check if error response contains detailed error info
        const result = error?.result;
        if (result && result.channelResults) {
          const failedResult = result.channelResults.find(
            (cr: IChannelMappingResult) => !cr.success && cr.error
          );
          
          if (failedResult) {
            setMappingErrorResult(failedResult);
            setShowMappingErrorModal(true);
            return;
          }
        }
        
        // Fallback to toast for generic errors
        showToast(error?.messageAr || t(T_KEYS.UNITS_MAPPING_ERROR_MERGE_FAILED), {
          severity: 'error',
        });
      },
    });
  }, [clearSelection, getMapPayloadWithAirbnb, mapUnits, showToast, t]);

  const handleMergeClick = useCallback(() => {
    if (canMergeWithMsool) {
      handleMergeConfirm();
    }
  }, [canMergeWithMsool, handleMergeConfirm]);

  const handleUnmapClick = useCallback((msoolUnit: IMsoolUnit) => {
    setUnmapTargetUnit(msoolUnit);
    setShowUnmapConfirmModal(true);
  }, []);

  // Handle Msool unit selection
  const handleSelectMsoolUnit = useCallback((unit: IMsoolUnit) => {
    setSelectedMsoolUnit(prev => prev?.propertyId === unit.propertyId ? null : unit);
  }, []);

  const handleUnmapConfirm = useCallback(() => {
    if (!unmapTargetUnit) return;
    
    // Filter out Airbnb units - they should never be unmapped
    const unmapPayload: IUnmapUnitsRequest = {
      propertyId: unmapTargetUnit.propertyId,
      units: (unmapTargetUnit.channels ?? [])
      .filter((channel: ILinkedUnit) => 
        channel.status && 
        channel.userChannelId !== null && 
        channel.channelType !== ChannelType.Airbnb // Exclude Airbnb
      )
      .map((channel: ILinkedUnit) => ({
        userChannelId: channel.userChannelId as number,
        channelType: channel.channelType,
        channelListingId: channel.channelListingId,
      })),
    };
    
    // Don't proceed if no units to unmap after filtering
    if (!unmapPayload.units || unmapPayload.units.length === 0) {
      setShowUnmapConfirmModal(false);
      setUnmapTargetUnit(null);
      return;
    }
    
    unmapUnits(unmapPayload, {
      onSuccess: () => {
        showToast(t(T_KEYS.UNITS_MAPPING_SUCCESS_UNMAPPED), { severity: 'success' });
        setShowUnmapConfirmModal(false);
        setUnmapTargetUnit(null);
      },
      onError: (error: any) => {
        showToast(error?.messageAr || t(T_KEYS.UNITS_MAPPING_ERROR_UNMAP_FAILED), {
          severity: 'error',
        });
      },
    });
  }, [unmapTargetUnit, unmapUnits, showToast, t]);

  const handleStartMapping = useCallback(() => {
    setShowOnboardingModal(true);
  }, []);

  // Show loading state
  if (isLoadingUnits) {
    return (
      <PageContainer>
        <MainContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress sx={{ color: '#5b3fff' }} />
          </Box>
        </MainContent>
      </PageContainer>
    );
  }

  // Show empty state if no units to map
  if (!hasUnitsToMap && !(msoolUnitsData?.units && msoolUnitsData.units.length > 0)) {
    return (
      <PageContainer>
        <MainContent>
          <HeaderSection onBackClick={handleBackToMain} isAddingUnits={isAddingUnits} />
          <PropertiesEmptyState />
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MainContent>
        <HeaderSection onBackClick={handleBackToMain} isAddingUnits={isAddingUnits} />
        <div>
          {msoolUnitsData?.units && msoolUnitsData.units.length > 0 && (
            <MsoolUnitsCard
              units={msoolUnitsData.units}
              isLoading={loadingMsool}
              selectedUnit={selectedMsoolUnit}
              onUnmap={handleUnmapClick}
              onSelectUnit={handleSelectMsoolUnit}
            />
          )}

          {Object.entries(channelConfig).map(([channelKey, config]) => {
            const units = allUnitsData[channelKey as keyof typeof allUnitsData] || [];
            const filteredUnits = getFilteredUnits(channelKey, units);
            const isExpanded = expandedChannels.has(channelKey);
            const isLoading = 
              (channelKey === 'booking' && loadingBooking) ||
              (channelKey === 'gathern' && loadingGathern);
            const selectedCount = selectedUnits.filter(u => u.channel === channelKey).length;
            
            // Hide the entire card if there are no units
            if (units.length === 0 && !isLoading) return null;

            // Check if this channel is already merged with the selected Msool unit
            const isChannelAlreadyMerged = selectedMsoolUnit?.channels?.some(
              (channel: ILinkedUnit) => channel.channelType === config.channelType && channel.status
            ) ?? false;
            
            // Disable when no Msool unit is selected OR when channel is already merged
            const isDisabled = !selectedMsoolUnit || isChannelAlreadyMerged;

            return (
              <ChannelCardComponent
                key={channelKey}
                channelKey={channelKey}
                config={config}
                units={units}
                filteredUnits={filteredUnits}
                isExpanded={isDisabled ? false : isExpanded}
                isLoading={isLoading}
                searchQuery={searchQueries[channelKey] || ''}
                statusFilter={statusFilters[channelKey] || 'all'}
                selectedCount={selectedCount}
                onToggleExpand={isDisabled ? () => {} : handleChannelToggle}
                onSearchChange={handleSearchChange}
                onStatusFilterChange={handleStatusFilterChange}
                onUnitToggle={handleUnitToggle}
                isUnitSelected={isUnitSelected}
                disabled={isDisabled}
              />
            );
          })}
        </div>
      </MainContent>

      <MergeButton
        selectionCount={totalSelectionCount}
        canMerge={canMergeWithMsool}
        onMerge={handleMergeClick}
        onClear={() => {
          clearSelection();
          setSelectedMsoolUnit(null);
        }}
        selectedChannelsCount={selectedChannels.length + (hasAirbnbSelected ? 1 : 0)}
        hasAirbnbSelected={hasAirbnbSelected}
        isLoading={isMapping}
      />

      <OnboardingModal
        open={showOnboardingModal}
        onClose={handleOnboardingClose}
        onStart={handleOnboardingStart}
      />

      <UnmapConfirmModal
        open={showUnmapConfirmModal}
        onClose={() => {
          setShowUnmapConfirmModal(false);
          setUnmapTargetUnit(null);
        }}
        onConfirm={handleUnmapConfirm}
        isLoading={isUnmapping}
        unit={unmapTargetUnit}
      />

      <MappingErrorModal
        open={showMappingErrorModal}
        onClose={() => {
          setShowMappingErrorModal(false);
          setMappingErrorResult(null);
        }}
        failedResult={mappingErrorResult}
      />
    </PageContainer>
  );
};

export default UnitsMappingView;
